import { api } from "@app/api";
import { createSelector } from "@app/fx";
import { defaultEntity, extractIdFromLink } from "@app/hal";
import { type WebState, schema } from "@app/schema";
import type { DeployEdge, LinkResponse } from "@app/types";
import { findAppById, selectApps } from "../app";
import type {
  AppDependency,
  DatabaseDependency,
  Dependency,
} from "../configuration";
import { findDatabaseById, selectDatabases } from "../database";

export interface DeployEdgeResponse {
  id: number;
  relationship_type: string;
  relationship_started_at: string | null;
  relationship_ended_at: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string;
  metadata: Record<string, string>;
  _links: {
    organization: LinkResponse;
    self: LinkResponse;
    source_resource: LinkResponse;
    destination_resource: LinkResponse;
  };
  _type: "edge";
}

export const defaultEdgeResponse = (
  e: Partial<DeployEdgeResponse> = {},
): DeployEdgeResponse => {
  const now = new Date().toISOString();
  return {
    id: 1,
    relationship_type: "",
    relationship_started_at: null,
    relationship_ended_at: null,
    created_at: now,
    updated_at: now,
    deleted_at: "",
    metadata: {},
    _links: {
      organization: { href: "" },
      self: { href: "" },
      source_resource: { href: "" },
      destination_resource: { href: "" },
    },
    _type: "edge",
    ...e,
  };
};

export const deserializeDeployEdge = (
  payload: DeployEdgeResponse,
): DeployEdge => {
  const links = payload._links;

  // Extract resource type from href (e.g., "https://api.aptible.com/apps/123" -> "apps")
  const extractResourceType = (link: LinkResponse): string => {
    if (!link.href) return "";
    const match = link.href.match(/\/([a-z_]+)\/\d+$/);
    return match ? match[1] : "";
  };

  return {
    id: `${payload.id}`,
    relationshipType: payload.relationship_type,
    relationshipStartedAt: payload.relationship_started_at,
    relationshipEndedAt: payload.relationship_ended_at,
    createdAt: payload.created_at,
    updatedAt: payload.updated_at,
    deletedAt: payload.deleted_at,
    organizationId: extractIdFromLink(links.organization),
    sourceResourceId: extractIdFromLink(links.source_resource),
    sourceResourceType: extractResourceType(links.source_resource),
    destinationResourceId: extractIdFromLink(links.destination_resource),
    destinationResourceType: extractResourceType(links.destination_resource),
    metadata: payload.metadata || {},
  };
};

export const selectEdgeById = schema.edges.selectById;
export const selectEdges = schema.edges.selectTable;
export const selectEdgesAsList = createSelector(
  schema.edges.selectTableAsList,
  (edges) => [...edges].sort((a, b) => a.id.localeCompare(b.id)),
);

export const selectEdgesBySourceResourceId = createSelector(
  selectEdgesAsList,
  (_: WebState, props: { resourceId: string }) => props.resourceId,
  (edges, resourceId) => {
    return edges.filter((edge) => edge.sourceResourceId === resourceId);
  },
);

export const selectEdgesByRelationshipType = createSelector(
  selectEdgesAsList,
  (_: WebState, props: { relationshipType: string }) => props.relationshipType,
  (edges, relationshipType) => {
    return edges.filter((edge) => edge.relationshipType === relationshipType);
  },
);

export const selectDependenciesByAppId = createSelector(
  selectEdgesAsList,
  selectApps,
  selectDatabases,
  (_: WebState, props: { id: string }) => props.id,
  (edges, apps, databases, id): Dependency[] => {
    const deps: Dependency[] = [];

    const appEdges = edges.filter(
      (edge) =>
        edge.sourceResourceId === id &&
        edge.relationshipType === "depends_on" &&
        !edge.deletedAt &&
        !edge.relationshipEndedAt,
    );

    appEdges.forEach((edge) => {
      const destType = edge.destinationResourceType;
      const destId = edge.destinationResourceId;
      const reason = edge.metadata?.discovered_in || "";

      if (destType === "apps") {
        const app = findAppById(apps, { id: destId });
        if (app?.id) {
          deps.push({
            type: "app",
            why: reason,
            refId: app.id,
            name: app.handle,
            resource: app,
          });
        }
      } else if (destType === "databases") {
        const db = findDatabaseById(databases, { id: destId });
        if (db?.id) {
          deps.push({
            type: "database",
            why: reason,
            refId: db.id,
            name: db.handle,
            resource: db,
          });
        }
      }
    });

    return deps;
  },
);

export const selectEdgeDependenciesByType = createSelector(
  selectDependenciesByAppId,
  (deps) => {
    const depGroups: { app: AppDependency[]; database: DatabaseDependency[] } =
      {
        app: [],
        database: [],
      };

    deps.forEach((dep) => {
      switch (dep.type) {
        case "app":
          depGroups.app.push(dep);
          break;
        case "database":
          depGroups.database.push(dep);
          break;
      }
    });

    return depGroups;
  },
);

export const fetchEdges = api.get<{
  rootResourceId: string;
  rootResourceType?: string;
  maxDistance?: number;
  onlyRelationshipTypes?: string[];
}>("/edges", function* (ctx, next) {
  const baseUrl = ctx.req().url;
  const url = new URL(baseUrl);

  url.searchParams.set("root_resource_id", ctx.payload.rootResourceId);
  url.searchParams.set(
    "root_resource_type",
    ctx.payload.rootResourceType || "App",
  );
  url.searchParams.set("max_distance", String(ctx.payload.maxDistance ?? 1));

  const types = ctx.payload.onlyRelationshipTypes || ["depends_on"];
  for (const type of types) {
    url.searchParams.append("only_relationship_types[]", type);
  }

  ctx.request = ctx.req({ url: url.toString() });

  yield* next();
});

export const edgeEntities = {
  edge: defaultEntity({
    id: "edge",
    deserialize: deserializeDeployEdge,
    save: schema.edges.add,
  }),
};
